
import { getCookie, isNumeric } from './helpers';

export default class CurrencyService
{

    // Mirrors CurrencyUtilities::getPriceFormatToUse() in commonobjects so JS and PHP prices match.
    static PRICE_FORMAT_COMMA_DOT_LOCALES = ['EN', 'AR', 'KO', 'JA', 'TL', 'ML', 'ZH', 'TH', 'MX'];
    static PRICE_FORMAT_SPACE_COMMA_LOCALES = ['FI', 'NO', 'SV', 'ET', 'RU', 'CS', 'LV', 'PL'];

    static getPriceFormat()
    {
        const locale = (getCookie('chosen_locale') ?? 'en').toUpperCase();

        if (this.PRICE_FORMAT_COMMA_DOT_LOCALES.includes(locale)) {
            return { thousand: ',', decimal: '.' };
        }
        if (this.PRICE_FORMAT_SPACE_COMMA_LOCALES.includes(locale)) {
            return { thousand: ' ', decimal: ',' };
        }

        return { thousand: '.', decimal: ',' };
    }

    /**
     * @param price {number}
     * @param decimals {number}
     * @return {string}
     */
    static formatPrice(price, decimals = 2)
    {
        if (! isNumeric(price)) {
            return 0;
        }

        const format = this.getPriceFormat();
        const [integerPart, decimalPart] = Number(price).toFixed(decimals).split('.');
        const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousand);

        return decimalPart ? `${groupedInteger}${format.decimal}${decimalPart}` : groupedInteger;
    }

    /**
     * @param price {string}
     * @returns {number}
     */
    static normalizePrice(price)
    {
        if (typeof price == 'number') {
            return price;
        }
        if (price == undefined || price == null) {
            return 0;
        }

        const separators = price.match(/[.,\s]/g);
        if (separators == null) {
            return parseFloat(price);
        }

        const lastSeparator = separators[separators.length - 1];
        const trailingDigits = price.length - price.lastIndexOf(lastSeparator) - 1;

        // A space is only ever used as a thousands separator (decimal is always
        // a comma in those locales). Repeating the SAME character more than once
        // (eg "1.700.000") can only be thousands grouping too -- a locale never
        // reuses one character for both thousands grouping and the decimal point.
        // For a single separator, thousands grouping always produces exactly 3
        // trailing digits (eg "17.000"), while this system's currencies only ever
        // configure 0-2 decimal places (eg "17.50") -- so 3 trailing digits after
        // a lone separator means thousands, not decimals.
        const isSpaceSeparator = /\s/.test(lastSeparator);
        const allSeparatorsMatch = separators.every(separator => separator === lastSeparator);
        const hasDecimalPart = !isSpaceSeparator && (
            (separators.length > 1 && !allSeparatorsMatch)
            || (separators.length === 1 && trailingDigits !== 3)
        );

        const groupingSeparators = hasDecimalPart ? separators.slice(0, -1) : separators;
        groupingSeparators.forEach(separator => {
            price = price.replace(separator, '');
        });

        if (hasDecimalPart) {
            price = price.replace(lastSeparator, '.');
        }

        return parseFloat(
            price.replace(/\s/g, '')
        );
    }

    static CURRENCY_PREFIX_LOCALES = [
        'EN',
        'AR',
        'KO',
        'JA',
        'TL',
        'ML',
        'ZH',
        'AU',
        'NZ',
        'CA',
        'IN',
        'PH',
        'MX',
        'BR',
        'CL'
    ];

    static isCurrencyPrefix(localeUpper) {
        const base = localeUpper.split(/[-_]/)[0];

        return this.CURRENCY_PREFIX_LOCALES.includes(base);
    }

    static shouldAddSpaceBetweenCurrencyAndAmount(currencyEntity) {
        if (typeof currencyEntity !== 'string' || currencyEntity.length === 0) {
            return false; // fallback safe
        }

        return /^[A-Z]{3}$/.test(currencyEntity);
    }

    static formatPriceWithCurrency(price, currencySymbol) {
        const locale = (getCookie('chosen_locale') ?? 'en').toUpperCase();

        const shouldAddSpace = this.shouldAddSpaceBetweenCurrencyAndAmount(currencySymbol);

        const space = shouldAddSpace ? ' ' : '';

        if (price == 0) {
            return '';
        }

        return this.isCurrencyPrefix(locale)
            ? `${currencySymbol}${space}${price}`
            : `${price}${space}${currencySymbol}`;
    }

}
